import { useRef, useEffect, useCallback } from "react"

interface AutoSaveOptions {
    interval?: number
    maxDuration?: number
    disabled?: boolean
    onSave: () => Promise<void>
    condition?: boolean
}

interface AutoSaveReturn {
    cancelAutoSave: () => void
    triggerSaveNow: () => Promise<void>
    isAutoSaveActive: boolean
    isAutoSaveInProgress: boolean
    resetAutoSave: () => void
}

/**
 * Custom hook for adding auto-save functionality to forms
 * @param options Auto-save configuration options
 * @returns Functions to control auto-save behavior
 */
export const useAutoSave = ({
    interval = 15000, // default: 15 seconds
    maxDuration = 300000, // default: 5 minutes
    disabled = false,
    onSave,
    condition = true,
}: AutoSaveOptions): AutoSaveReturn => {
    const autoSaveSetupRef = useRef<boolean>(false)
    const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null)
    const autoSaveStartTimeRef = useRef<number>(0)
    const autoSaveInProgressRef = useRef<boolean>(false)

    // Function to cancel auto-save
    const cancelAutoSave = useCallback((): void => {
        if (autoSaveTimerRef.current) {
            clearInterval(autoSaveTimerRef.current)
            autoSaveTimerRef.current = null
            // Reset the setup flag to allow auto-save to be set up again if needed
            autoSaveSetupRef.current = false
        }
    }, [])

    // Setup auto-save
    useEffect(() => {
        // Always clear any existing timer first to prevent multiple instances
        if (autoSaveTimerRef.current) {
            clearInterval(autoSaveTimerRef.current)
            autoSaveTimerRef.current = null
        }

        if (disabled) {
            autoSaveSetupRef.current = false
            return undefined
        }

        autoSaveSetupRef.current = true
        autoSaveStartTimeRef.current = Date.now()

        // Set up auto-save interval
        autoSaveTimerRef.current = setInterval(() => {
            // Check if max duration exceeded
            const timeElapsed = Date.now() - autoSaveStartTimeRef.current
            if (timeElapsed > maxDuration) {
                cancelAutoSave()
                return
            }

            if (autoSaveInProgressRef.current || !condition) {
                return
            }

            autoSaveInProgressRef.current = true
            // Wrap onSave in Promise.resolve to ensure we have a valid Promise
            Promise.resolve()
                .then(() => onSave())
                .catch((err) => {
                    console.error("Error during auto-save:", err)
                })
                .finally(() => {
                    autoSaveInProgressRef.current = false
                })
        }, interval)

        return () => {
            cancelAutoSave()
        }
    }, [disabled, onSave, interval, maxDuration, condition, cancelAutoSave])

    return {
        cancelAutoSave,
        triggerSaveNow: async (): Promise<void> => {
            if (!autoSaveInProgressRef.current && condition && !disabled) {
                autoSaveInProgressRef.current = true
                try {
                    // Ensure we properly handle the promise
                    await Promise.resolve(onSave())
                } catch (err) {
                    console.error("Error during manual save:", err)
                } finally {
                    autoSaveInProgressRef.current = false
                }
            }
        },
        isAutoSaveActive: autoSaveSetupRef.current && !disabled,
        isAutoSaveInProgress: autoSaveInProgressRef.current,
        resetAutoSave: (): void => {
            // Cancel the current timer
            cancelAutoSave()

            // Only restart if not disabled
            if (!disabled) {
                // Reset start time
                autoSaveStartTimeRef.current = Date.now()
                autoSaveSetupRef.current = true

                autoSaveTimerRef.current = setInterval(() => {
                    const timeElapsed =
                        Date.now() - autoSaveStartTimeRef.current
                    if (timeElapsed > maxDuration) {
                        cancelAutoSave()
                        return
                    }

                    if (!autoSaveInProgressRef.current && condition) {
                        autoSaveInProgressRef.current = true
                        // Also wrap onSave in Promise.resolve here
                        Promise.resolve()
                            .then(() => onSave())
                            .catch((err) =>
                                console.error("Error during auto-save:", err)
                            )
                            .finally(() => {
                                autoSaveInProgressRef.current = false
                            })
                    }
                }, interval)
            }
        },
    }
}
